// Captions saved for later.
//
// The same `content_drafts` rows the studio writes, the caption library lists
// and "Use a saved draft" offers on the composer — one library of words, read
// here rather than copied. "Continue editing" opens the composer with the
// caption already in the box, which is the only useful thing a draft on a
// dashboard can do.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listDrafts } from "../lib/api";
import { Loading } from "../components/Spinner";
import { logError } from "../lib/logger";
import type { ContentDraft } from "../lib/types";

const SHOWN = 4;

/** "2 hours ago" — the only form a draft's age is ever read in. */
function ago(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** The first line, which is what a caption is recognised by. */
const firstLine = (body: string) => {
  const line = body.trim().split("\n")[0] ?? "";
  return line.length > 90 ? `${line.slice(0, 90).trimEnd()}…` : line || "Empty draft";
};

export function DraftsCard({ workspaceId }: { workspaceId: string | null }) {
  const [drafts, setDrafts] = useState<ContentDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setDrafts(await listDrafts(workspaceId, "caption", SHOWN));
      setError(null);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "dashboard_drafts", workspaceId });
    }
  }, [workspaceId]);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="card dash-card drafts-card" aria-labelledby="drafts-head">
      <div className="card-head">
        <h2 id="drafts-head">Drafts</h2>
        <Link to="/captions" className="card-head-link">View all →</Link>
      </div>

      {error ? (
        <p className="err empty-state">
          Your drafts could not be loaded.{" "}
          <Link to="/captions">Open the caption library</Link> — they are still there.
        </p>
      ) : !drafts ? (
        <Loading label="Reading your drafts…" />
      ) : drafts.length === 0 ? (
        <p className="muted empty-state">
          No drafts yet. Captions you save for later appear here — write one above and press
          Save draft, or <Link to="/captions">bring a whole spreadsheet in</Link>.
        </p>
      ) : (
        <ul className="drafts-list">
          {drafts.map((d) => (
            <li key={d.id}>
              <span className="drafts-what">
                <strong>{firstLine(d.body)}</strong>
                <span className="muted">Edited {ago(d.updated_at ?? d.created_at)}</span>
              </span>
              <span className="chip">{d.source === "ai" ? "AI" : "Draft"}</span>
              {/* Not a link: the caption travels in state rather than in the
                  URL, because a caption is long, private and full of the
                  characters a query string handles worst. */}
              <button type="button" className="sm"
                      onClick={() => navigate("/compose", { state: { draft: d.body } })}>
                Continue editing
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
